import React, { Component } from 'react';
import ReactPaginate from 'react-paginate';
import { Link } from 'react-router-dom';
import '../Pagination.css';
import '../common/MdPage.css';
import * as constantClass from '../fileWithConstants';
import { createPagedGridController } from '../../hooks/pagedGridClassAdapter';
import { displayDate } from '../../services/dates/dateService';
import * as inscripcionesService from '../../services/inscripciones/inscripcionesService';
import {
  classifyInscripcionesError,
  formatInscripcionExamenCondicion,
  formatInscripcionExamenPeriodoGrid,
  getInscripcionExamenEstadoLabel,
} from '../../services/inscripciones/inscripcionesDto';
import { ConfirmDialog } from '../common/ConfirmDialog';
import {
  MdRowActions,
  MdTableActionsCell,
  MdTableCell,
} from '../common/MdRowActions';
import { PlusIcon, SearchIcon } from '../common/mdPageIcons';

export class MDInscripcionExamen extends Component {
  static displayName = MDInscripcionExamen.name;

  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      searchInput: '',
      perPage: 10,
      paginationData: [],
      pageCount: 0,
      forcePage: 0,
      showErrorMessage: false,
      errorMessage: '',
      successMessage: '',
      deletingId: null,
      pendingDelete: null,
    };
    this.grid = createPagedGridController({
      fetchPage: (query) => inscripcionesService.listInscripcionesExamen(query),
      defaultPageSize: 10,
      onChange: (gridState) => this.applyGridState(gridState),
    });
    this.searchInputRef = React.createRef();
  }

  applyGridState(gridState) {
    this.setState({
      paginationData: gridState.items,
      pageCount: gridState.pageCount,
      searchInput: gridState.search,
      perPage: gridState.pageSize,
      loading: gridState.loading,
      forcePage: gridState.forcePage,
    });
  }

  async componentDidMount() {
    const canAccess = await constantClass.canAccess('WWInscripcionExamen');
    if (canAccess) {
      this.populateData();
    }
  }

  handlePageClick = (e) => {
    this.grid.handlePageClick(e).catch((error) => this.handleInscripcionesError(error));
  };

  handleSearchInputChange = (e) => {
    const searchInput = e.target.value;
    const cleared = searchInput.trim() === '';
    this.setState({ searchInput, successMessage: '', showErrorMessage: false, errorMessage: '' });
    if (cleared && this.grid.getState().search.trim() !== '') {
      this.grid.clearSearchIfNeeded(this.grid.getState().search).catch((error) => this.handleInscripcionesError(error));
    }
  };

  handleSearchSubmit = (e) => {
    e.preventDefault();
    const search = (this.searchInputRef.current
      ? this.searchInputRef.current.value
      : this.state.searchInput).trim();
    this.setState({ searchInput: search });
    this.grid.submitSearch(search).catch((error) => this.handleInscripcionesError(error));
  };

  handleRemoveItem = (periodo) => {
    if (this.state.deletingId) {
      return;
    }
    this.setState({
      pendingDelete: {
        id: periodo.inscripcionExamenCod,
        nombre: (periodo.alumno || '').trim(),
        materia: (periodo.materia || '').trim(),
        periodo: formatInscripcionExamenPeriodoGrid(periodo),
      },
    });
  };

  handleCancelDelete = () => {
    if (this.state.deletingId) {
      return;
    }
    this.setState({ pendingDelete: null });
  };

  handleConfirmDelete = () => {
    const { pendingDelete } = this.state;
    if (!pendingDelete || this.state.deletingId) {
      return;
    }
    this.deleteItem(pendingDelete.id);
  };

  handleModificarEstado = (id) => {
    this.changeItem(id);
  };

  buildRowActions(periodo) {
    const id = periodo.inscripcionExamenCod;
    const estado = periodo.inscripcionExamenEstado;
    const actions = [];

    if (estado === 'P' || estado === 'A') {
      actions.push({
        key: 'edit',
        label: 'Modificar',
        variant: 'primary',
        icon: 'pencil',
        to: '/update-ie/' + id + '/0',
      });
      actions.push({
        key: 'delete',
        label: this.state.deletingId === id ? 'Eliminando...' : 'Borrar',
        variant: 'danger',
        icon: 'trash',
        onClick: () => this.handleRemoveItem(periodo),
        disabled: this.state.loading || this.state.deletingId === id,
      });
    }

    actions.push({
      key: 'detail',
      label: 'Detalles',
      variant: 'outline',
      icon: 'info',
      to: '/update-ie/' + id + '/1',
    });

    if (estado === 'P') {
      actions.push({
        key: 'activate',
        label: 'Activar',
        variant: 'primary',
        onClick: () => this.handleModificarEstado(id),
      });
    }

    return actions;
  }

  renderTable(periodos) {
    return (
      <div className="md-page__table-card md-ie__table-card">
        <div className="md-ie__table-scroll">
          <table className="md-page__table md-page__table--actions-fixed md-page__table--ie-grid" aria-labelledby="md-ie-title">
            <thead>
              <tr>
                <th scope="col" className="md-page__table__col-alumno">Alumno</th>
                <th scope="col" className="md-page__table__col-fecha-inscripcion">Fecha de inscripción</th>
                <th scope="col" className="md-page__table__col-estado">Estado</th>
                <th scope="col" className="md-page__table__col-materia">Materia</th>
                <th scope="col" className="md-page__table__col-condicion">Condición</th>
                <th scope="col" className="md-page__table__col-fecha-examen">Fecha examen</th>
                <th scope="col" className="md-page__table__col-periodo-examen">Período de examen</th>
                <th scope="col" className="md-page__table__actions-header" aria-label="Acciones" />
              </tr>
            </thead>
            <tbody>
              {periodos.map((periodo) => (
                <tr key={periodo.inscripcionExamenCod}>
                  <MdTableCell className="md-page__table__col-alumno">{periodo.alumno}</MdTableCell>
                  <MdTableCell className="md-page__table__col-fecha-inscripcion">
                    {displayDate(periodo.inscripcionExamenFechaHora)}
                  </MdTableCell>
                  <MdTableCell className="md-page__table__col-estado">
                    {getInscripcionExamenEstadoLabel(periodo.inscripcionExamenEstado)}
                  </MdTableCell>
                  <MdTableCell className="md-page__table__col-materia">{periodo.materia}</MdTableCell>
                  <MdTableCell className="md-page__table__col-condicion">
                    {formatInscripcionExamenCondicion(periodo.inscripcionExamenCondicionInsc)}
                  </MdTableCell>
                  <MdTableCell className="md-page__table__col-fecha-examen">
                    {displayDate(periodo.fechaExamen)}
                  </MdTableCell>
                  <MdTableCell className="md-page__table__col-periodo-examen">
                    {formatInscripcionExamenPeriodoGrid(periodo)}
                  </MdTableCell>
                  <MdTableActionsCell>
                    <MdRowActions items={this.buildRowActions(periodo)} />
                  </MdTableActionsCell>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  render() {
    const isBusy = this.state.loading;
    const listLoading = this.state.loading && !this.state.deletingId;
    const { pendingDelete } = this.state;

    const contents = listLoading
      ? <p className="md-page__loading"><em>Cargando datos...</em></p>
      : this.renderTable(this.state.paginationData);

    return (
      <div className="md-page md-ie">
        <div className="md-page__header">
          <h1 id="md-ie-title" className="md-page__title">Manejar Datos de Inscripciones a Examenes</h1>
        </div>

        {!this.state.showErrorMessage ? (
          <div className="md-page__toolbar">
            <Link className="md-page__action-link" to="/add-ie">
              <PlusIcon />
              Nueva Inscripción a Examen
            </Link>
          </div>
        ) : null}

        <div className="md-page__feedback" aria-live="polite" role="status">
          {this.state.showErrorMessage ? (
            <div className="md-page__error" role="alert">{this.state.errorMessage}</div>
          ) : null}
          {this.state.successMessage ? (
            <div className="md-page__success">{this.state.successMessage}</div>
          ) : null}
        </div>

        {!this.state.showErrorMessage ? (
          <>
            <form className="md-page__search-row" onSubmit={this.handleSearchSubmit}>
              <div className="md-page__search-wrap">
                <span className="md-page__search-icon">
                  <SearchIcon />
                </span>
                <input
                  ref={this.searchInputRef}
                  className="md-page__search-input"
                  type="text"
                  name="search"
                  autoComplete="off"
                  disabled={isBusy}
                  placeholder="Ingrese Alumno o Materia"
                  value={this.state.searchInput}
                  onChange={this.handleSearchInputChange}
                />
              </div>
              <button type="submit" className="md-page__search-btn" disabled={isBusy}>
                Buscar
              </button>
            </form>

            {contents}

            {!listLoading && this.state.pageCount > 1 ? (
              <div className="md-page__pagination">
                <ReactPaginate
                  previousLabel="Anterior"
                  nextLabel="Siguiente"
                  breakLabel="..."
                  breakClassName="break-me"
                  pageCount={this.state.pageCount}
                  marginPagesDisplayed={2}
                  pageRangeDisplayed={5}
                  onPageChange={this.handlePageClick}
                  forcePage={this.state.forcePage}
                  containerClassName="pagination"
                  subContainerClassName="pages pagination"
                  activeClassName="active"
                />
              </div>
            ) : null}
          </>
        ) : null}

        <ConfirmDialog
          open={Boolean(pendingDelete)}
          title="Eliminar inscripción a examen"
          message="Esta acción eliminará la inscripción y no se puede deshacer."
          detail={
            pendingDelete
              ? `${pendingDelete.nombre} — ${pendingDelete.materia} (${pendingDelete.periodo})`
              : ''
          }
          confirmLabel="Eliminar"
          cancelLabel="Cancelar"
          variant="danger"
          loading={Boolean(this.state.deletingId)}
          onConfirm={this.handleConfirmDelete}
          onCancel={this.handleCancelDelete}
        />
      </div>
    );
  }

  handleInscripcionesError(error) {
    const outcome = classifyInscripcionesError(error);
    if (outcome.type === 'timeout') {
      constantClass.RedirectToLogin();
      return;
    }
    this.setState({
      showErrorMessage: true,
      errorMessage: outcome.message,
      loading: false,
    });
  }

  async populateData() {
    try {
      await this.grid.load();
    } catch (error) {
      this.handleInscripcionesError(error);
    }
  }

  async changeItem(id) {
    this.setState({ loading: true, showErrorMessage: false, errorMessage: '' });
    try {
      await inscripcionesService.changeStatusInscripcionExamen(id);
      this.setState({ successMessage: 'La inscripción se activó correctamente.' });
      await this.grid.reloadAfterDelete();
    } catch (error) {
      this.handleInscripcionesError(error);
    }
  }

  async deleteItem(id) {
    this.setState({ deletingId: id, showErrorMessage: false, errorMessage: '' });
    try {
      await inscripcionesService.deleteInscripcionExamen(id);
      this.setState({ successMessage: 'El registro se eliminó correctamente.', pendingDelete: null });
      await this.grid.reloadAfterDelete(this.state.paginationData.length);
    } catch (error) {
      this.handleInscripcionesError(error);
    } finally {
      this.setState({ deletingId: null });
    }
  }
}
